import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { Database, Plus, Trash2, CheckCircle2, Loader2, KeyRound } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * S3 / Wasabi accounts panel.
 * Renders the list + an inline "add account" form with a test-connection step
 * BEFORE the credentials are ever sent to the server for persistence.
 */
export default function S3AccountsPanel() {
  const { t } = useI18n();
  const [accounts, setAccounts] = useState(null);
  const [regions, setRegions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [confirm, setConfirm] = useState(null);

  // Form state
  const blank = { name: "", bucket: "", region: "us-east-1", access_key: "", secret_key: "" };
  const [form, setForm] = useState(blank);
  const [testing, setTesting] = useState(false);
  const [tested, setTested] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, r] = await Promise.all([
      api.get("/s3/accounts"),
      api.get("/s3/regions"),
    ]);
    setAccounts(a.data);
    setRegions(r.data.regions || []);
  };

  useEffect(() => {
    load();
  }, []);

  const onTest = async () => {
    setTesting(true);
    setTested(false);
    try {
      await api.post("/s3/test-connection", {
        bucket: form.bucket.trim(),
        region: form.region,
        access_key: form.access_key.trim(),
        secret_key: form.secret_key.trim(),
      });
      setTested(true);
      toast.success(t("s3.testOk"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.post("/s3/accounts", {
        name: form.name.trim() || form.bucket.trim(),
        bucket: form.bucket.trim(),
        region: form.region,
        access_key: form.access_key.trim(),
        secret_key: form.secret_key.trim(),
      });
      toast.success(t("s3.saved"));
      setForm(blank);
      setTested(false);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (id) => {
    try {
      await api.delete(`/s3/accounts/${id}`);
      toast.success(t("s3.removed"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setConfirm(null);
    }
  };

  const formValid =
    form.bucket.trim().length > 0 &&
    form.access_key.trim().length > 0 &&
    form.secret_key.trim().length > 0;

  return (
    <div className="space-y-6" data-testid="s3-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-mono text-xs uppercase text-muted-foreground">
          <Database className="h-4 w-4 text-[#FF3D00]" />
          {t("s3.subtitle")}
        </div>
        {!showForm && (
          <Button
            variant="outline"
            className="rounded-sm gap-2"
            onClick={() => setShowForm(true)}
            data-testid="add-s3-account-button"
          >
            <Plus className="h-4 w-4" /> {t("s3.add")}
          </Button>
        )}
      </div>

      {/* List */}
      {accounts === null && (
        <div className="font-mono text-sm text-muted-foreground">{t("common.loading")}</div>
      )}
      {accounts && accounts.length === 0 && !showForm && (
        <div className="border border-dashed border-border bg-card p-10 text-center">
          <Database className="mx-auto h-8 w-8 text-[#FF3D00]" />
          <h3 className="mt-4 font-display text-lg font-bold">{t("s3.empty")}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{t("s3.emptyHint")}</p>
        </div>
      )}
      {accounts && accounts.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {accounts.map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-4 border border-border bg-card p-4"
              data-testid={`s3-account-${a.id}`}
            >
              <div className="flex h-10 w-10 items-center justify-center bg-[#FF3D00] text-white">
                <Database className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-display font-bold tracking-tight">{a.name}</div>
                <div className="font-mono text-[11px] uppercase text-muted-foreground">
                  Wasabi · {a.region}
                </div>
                <div className="truncate font-mono text-xs text-muted-foreground">
                  s3://{a.bucket}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-sm"
                onClick={() => setConfirm(a)}
                data-testid={`remove-s3-account-${a.id}`}
                title={t("common.delete")}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="border border-border bg-card p-5" data-testid="s3-add-form">
          <div className="mb-4 flex items-center gap-2 font-mono text-xs uppercase text-muted-foreground">
            <KeyRound className="h-4 w-4" /> {t("s3.addTitle")}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="font-mono text-xs uppercase text-muted-foreground">
                {t("s3.fieldName")}
              </Label>
              <Input
                value={form.name}
                onChange={(e) => {
                  setForm({ ...form, name: e.target.value });
                  setTested(false);
                }}
                placeholder={t("s3.fieldNamePh")}
                className="mt-2 rounded-sm h-11"
                data-testid="s3-name-input"
              />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase text-muted-foreground">
                {t("s3.fieldBucket")}
              </Label>
              <Input
                value={form.bucket}
                onChange={(e) => {
                  setForm({ ...form, bucket: e.target.value });
                  setTested(false);
                }}
                placeholder="my-backup-bucket"
                className="mt-2 rounded-sm h-11 font-mono text-sm"
                data-testid="s3-bucket-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono text-xs uppercase text-muted-foreground">
                {t("s3.fieldRegion")}
              </Label>
              <Select
                value={form.region}
                onValueChange={(v) => {
                  setForm({ ...form, region: v });
                  setTested(false);
                }}
              >
                <SelectTrigger className="mt-2 rounded-sm h-11" data-testid="s3-region-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm max-h-72">
                  {regions.map((r) => (
                    <SelectItem key={r.code} value={r.code}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase text-muted-foreground">
                {t("s3.fieldAccessKey")}
              </Label>
              <Input
                value={form.access_key}
                onChange={(e) => {
                  setForm({ ...form, access_key: e.target.value });
                  setTested(false);
                }}
                placeholder="WASABI_ACCESS_KEY_..."
                className="mt-2 rounded-sm h-11 font-mono text-xs"
                data-testid="s3-access-key-input"
              />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase text-muted-foreground">
                {t("s3.fieldSecretKey")}
              </Label>
              <Input
                type="password"
                value={form.secret_key}
                onChange={(e) => {
                  setForm({ ...form, secret_key: e.target.value });
                  setTested(false);
                }}
                placeholder="••••••••"
                className="mt-2 rounded-sm h-11 font-mono text-xs"
                data-testid="s3-secret-key-input"
              />
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="rounded-sm gap-2"
              onClick={onTest}
              disabled={!formValid || testing}
              data-testid="s3-test-button"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t("s3.test")}
            </Button>
            <Button
              className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2"
              onClick={onSave}
              disabled={!formValid || saving}
              data-testid="s3-save-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t("s3.save")}
            </Button>
            <Button
              variant="ghost"
              className="rounded-sm"
              onClick={() => {
                setShowForm(false);
                setForm(blank);
                setTested(false);
              }}
              data-testid="s3-cancel-button"
            >
              {t("common.cancel")}
            </Button>
            {tested && (
              <span className="ml-auto inline-flex items-center gap-1 font-mono text-xs text-emerald-600">
                <CheckCircle2 className="h-4 w-4" /> {t("s3.connected")}
              </span>
            )}
          </div>

          <p className="mt-4 text-[11px] text-muted-foreground">{t("s3.security")}</p>
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("s3.removeTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("s3.removeConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-remove-s3">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => onRemove(confirm.id)}
              data-testid="confirm-remove-s3"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
